import { jsxs, jsx } from 'react/jsx-runtime';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useTheme } from 'react-jss';
import { uniqueId, clsx, isBrowser, getDisplayName, safeRest, handlePortal } from '@wds/utils';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var TOOLTIP_PLACEMENTS = ['bottom', 'left', 'right', 'top'];

var useStyles = createMemoStyles(function (theme) {
  var _a, _b;
  var breakpoints = theme.breakpoints,
    motion = theme.motion,
    spacing = theme.spacing,
    palette = theme.palette,
    shadow = theme.shadow,
    type = theme.type,
    radius = theme.radius;
  var tooltip = {
    bgColor: palette.background.dark,
    typeColor: palette.text.light,
    borderRadius: radius.small,
    pipSize: 12,
    fontSize: type.fontSize.bodySm,
    padding: [spacing[4]],
    fontWeight: type.fontWeight.regular,
    boxShadow: shadow.none
  };
  var halfPipSize = tooltip.pipSize / 2;
  var tooltipTransform = function tooltipTransform(placement) {
    return {
      top: 'translate(-50%, 0)',
      bottom: "translate(-50%, ".concat(spacing[1], "px)"),
      left: 'translate(0, -50%)',
      right: 'translate(0, -50%)'
    }[placement];
  };
  var tooltipTop = function tooltipTop(placement) {
    return {
      top: 'auto',
      bottom: '100%',
      left: '50%',
      right: '50%'
    }[placement];
  };
  var tooltipBottom = function tooltipBottom(placement) {
    return {
      top: '100%',
      bottom: 'auto',
      left: 'auto',
      right: 'auto'
    }[placement];
  };
  var tooltipLeft = function tooltipLeft(placement) {
    return {
      top: '50%',
      bottom: '50%',
      left: 'auto',
      right: '100%'
    }[placement];
  };
  var tooltipAlign = function tooltipAlign(placement) {
    return {
      top: 'center',
      bottom: 'center',
      left: 'right',
      right: 'left'
    }[placement];
  };
  var tooltipBodyLeft = function tooltipBodyLeft(placement) {
    return {
      top: '50%',
      bottom: '50%',
      left: 'auto',
      right: -halfPipSize
    }[placement];
  };
  var tooltipBodyTop = function tooltipBodyTop(placement) {
    return {
      top: 'auto',
      bottom: -halfPipSize,
      left: '50%',
      right: '50%'
    }[placement];
  };
  var TooltipStyles = {
    wrenchTooltipContainer: {
      boxSizing: 'border-box',
      position: 'relative',
      outline: 'none',
      padding: 0,
      margin: 0,
      display: function (_a) {
        var stretched = _a.stretched;
        return stretched ? 'block' : undefined;
      },
      width: function (_a) {
        var stretched = _a.stretched;
        return stretched ? '100%' : undefined;
      }
    },
    wrenchTooltip: (_a = {
      boxSizing: 'border-box',
      padding: 0,
      margin: 0,
      zIndex: function (_a) {
        var elevation = _a.elevation;
        return elevation !== null && elevation !== void 0 ? elevation : theme.elevation.sky;
      },
      position: 'absolute',
      width: 300,
      outline: 'none',
      pointerEvents: 'none',
      whiteSpace: 'normal',
      opacity: function (_a) {
        var show = _a.show;
        return show ? 1 : 0;
      },
      left: '50%',
      top: function (_a) {
        var placement = _a.placement;
        return placement === 'bottom' ? '100%' : 'auto';
      },
      bottom: function (_a) {
        var placement = _a.placement;
        return placement === 'bottom' ? 'auto' : '100%';
      },
      marginTop: function (_a) {
        var placement = _a.placement;
        return placement === 'bottom' ? tooltip.pipSize : 0;
      },
      transform: function (_a) {
        var show = _a.show,
          placement = _a.placement;
        if (show) {
          if (placement === 'bottom') {
            return "translate(-50%, ".concat(spacing[1], "px)");
          }
          return 'translate(-50%, 0)';
        }
        return "translate(-50%, -".concat(spacing[1], "px)");
      },
      textAlign: 'center',
      marginBottom: tooltip.pipSize,
      // delay
      transitionDelay: function (_a) {
        var delay = _a.delay;
        return delay ? '1000ms' : 'none';
      },
      // show transition
      transition: function (_a) {
        var show = _a.show;
        return show ? "transform 250ms ".concat(motion.curve.enter, ", opacity 50ms linear 50ms") : 'all 0s ease 0s';
      }
    }, _a["@media (min-width: ".concat(breakpoints.md, "px)")] = {
      transform: function (_a) {
        var placement = _a.placement;
        return placement ? tooltipTransform(placement) : undefined;
      },
      top: function (_a) {
        var placement = _a.placement;
        return placement ? tooltipTop(placement) : undefined;
      },
      bottom: function (_a) {
        var placement = _a.placement;
        return placement ? tooltipBottom(placement) : undefined;
      },
      left: function (_a) {
        var placement = _a.placement;
        return placement ? tooltipLeft(placement) : undefined;
      },
      right: function (_a) {
        var placement = _a.placement;
        return placement === 'left' ? '100%' : 'auto';
      },
      /**
       * TODO: Need to make pipSize a rem value if we want to define in REM
       */
      marginLeft: function (_a) {
        var placement = _a.placement;
        return placement === 'right' ? spacing[4] : 0;
      },
      // tooltip.pipSize + spacing[1]
      marginRight: function (_a) {
        var placement = _a.placement;
        return placement === 'left' ? spacing[4] : 0;
      },
      // tooltip.pipSize + spacing[1]
      textAlign: function (_a) {
        var placement = _a.placement;
        return placement ? tooltipAlign(placement) : undefined;
      }
    }, _a),
    wrenchTooltipBody: {
      fontFamily: type.fontFamily.base,
      '-webkit-font-smoothing': 'antialiased',
      display: 'inline-block',
      borderRadius: tooltip.borderRadius,
      background: tooltip.bgColor,
      color: tooltip.typeColor,
      fontSize: tooltip.fontSize,
      padding: tooltip.padding,
      lineHeight: 1.5,
      textAlign: 'left',
      fontWeight: tooltip.fontWeight,
      boxShadow: tooltip.boxShadow,
      '&:after': (_b = {
        bottom: function (_a) {
          var placement = _a.placement;
          return placement === 'bottom' ? 'auto' : -halfPipSize;
        },
        left: '50%',
        marginLeft: -halfPipSize,
        content: '""',
        position: 'absolute',
        width: tooltip.pipSize,
        height: tooltip.pipSize,
        backgroundColor: tooltip.bgColor,
        transform: 'rotate(45deg)'
      }, _b["@media (min-width: ".concat(breakpoints.md, "px)")] = {
        top: function (_a) {
          var placement = _a.placement;
          return placement ? tooltipBodyTop(placement) : undefined;
        },
        left: function (_a) {
          var placement = _a.placement;
          return placement ? tooltipBodyLeft(placement) : undefined;
        },
        right: function (_a) {
          var placement = _a.placement;
          return placement === 'left' ? -halfPipSize : 'auto';
        },
        marginTop: function (_a) {
          var placement = _a.placement;
          return placement === 'right' || placement === 'left' ? -halfPipSize : 0;
        },
        marginLeft: function (_a) {
          var placement = _a.placement;
          return placement === 'right' ? 0 : -halfPipSize;
        }
      }, _b["@media (max-width: ".concat(breakpoints.md, "px)")] = {
        top: function (_a) {
          var placement = _a.placement;
          return placement === 'bottom' ? -halfPipSize : 'auto';
        }
      }, _b)
    },
    wrenchTooltipBodyCaret: {
      '&:after': {
        border: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? '0px' : undefined;
        },
        clip: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? 'rect(1px, 1px, 1px, 1px)' : undefined;
        },
        height: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? '1px' : undefined;
        },
        margin: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? '-1px' : undefined;
        },
        overflow: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? 'hidden' : undefined;
        },
        padding: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? 0 : undefined;
        },
        position: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? 'absolute' : undefined;
        },
        width: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? '1px' : undefined;
        },
        whiteSpace: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? 'nowrap' : undefined;
        },
        wordWrap: function (_a) {
          var hideCaret = _a.hideCaret;
          return hideCaret ? 'normal' : undefined;
        }
      }
    }
  };
  return TooltipStyles;
});

var Tooltip = function Tooltip(_a) {
  var _b = _a.delay,
    delay = _b === void 0 ? false : _b,
    _c = _a.hideCaret,
    hideCaret = _c === void 0 ? false : _c,
    _d = _a.placement,
    placement = _d === void 0 ? 'top' : _d,
    text = _a.text,
    _e = _a.show,
    propShow = _e === void 0 ? false : _e,
    _f = _a.disableHover,
    disableHover = _f === void 0 ? false : _f,
    renderTo = _a.renderTo,
    onChange = _a.onChange,
    alwaysShow = _a.alwaysShow,
    children = _a.children,
    elevation = _a.elevation,
    rest = __rest(_a, ["delay", "hideCaret", "placement", "text", "show", "disableHover", "renderTo", "onChange", "alwaysShow", "children", "elevation"]);
  var _g = useState(false),
    show = _g[0],
    setShow = _g[1];
  var _h = useState({}),
    tooltipStyle = _h[0],
    setTooltipStyle = _h[1];
  var _j = useState(false),
    stretched = _j[0],
    setStretched = _j[1];
  var theme = useTheme();
  var classes = useStyles({
    show: propShow || show || alwaysShow,
    placement: placement,
    hideCaret: hideCaret,
    stretched: stretched,
    delay: delay,
    elevation: elevation
  });
  // Refs
  var tooltipRef = useRef(null);
  var tooltipContentRef = useRef(null);
  var uniqueTooltipName = useMemo(function () {
    return uniqueId('wds-tooltip-');
  }, []);
  var tooltipBodyClasses = clsx(classes.wrenchTooltipBody, classes.wrenchTooltipBodyCaret);
  // Set to 800 by default
  var TOOLTIP_TIMEOUT;
  var updatePosition = function updatePosition() {
    var _a;
    if (tooltipRef.current && show && tooltipContentRef.current && tooltipContentRef.current.children && tooltipContentRef.current.children.length) {
      var rect = tooltipRef.current.getBoundingClientRect();
      var tooltipBodyRect = tooltipContentRef.current.children[0].getBoundingClientRect();
      var tooltipRect = tooltipContentRef.current.getBoundingClientRect();
      var bottom = 'auto';
      var pipSize = placement === 'top' ? theme.spacing[2] : theme.spacing[4];
      var screenPlacement = window.innerWidth <= theme.breakpoints.md && (placement === TOOLTIP_PLACEMENTS[2] || placement === TOOLTIP_PLACEMENTS[3]) ? TOOLTIP_PLACEMENTS[0] : placement;
      var position = (_a = {
        top: {
          top: rect.top - tooltipBodyRect.height - rect.height / 2 - pipSize,
          left: rect.left + rect.width / 2
        },
        left: {
          top: rect.top + rect.height / 2,
          left: rect.left - (tooltipRect.width + 16)
        },
        right: {
          top: rect.top + rect.height / 2,
          left: rect.left + rect.width
        },
        bottom: {
          top: rect.bottom,
          left: rect.left + rect.width / 2
        }
      }[screenPlacement]) !== null && _a !== void 0 ? _a : {
        top: rect.bottom,
        left: rect.left + rect.width / 2
      };
      var z = (elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space) + 1;
      setTooltipStyle({
        top: position.top,
        left: position.left,
        zIndex: z,
        position: 'fixed',
        bottom: bottom
      });
    }
  };
  var handleScroll = function handleScroll() {
    if (show && renderTo) {
      updatePosition();
    }
  };
  // componentDidMount
  useEffect(function () {
    if (show && renderTo) {
      updatePosition();
    }
    if (isBrowser && renderTo && show) {
      updatePosition();
    }
    if (disableHover && isBrowser) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationchange', handleScroll);
    }
    // componentWillUnmount
    return function () {
      if (disableHover && renderTo && isBrowser) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('orientationchange', handleScroll);
      }
      if (delay) {
        clearTimeout(TOOLTIP_TIMEOUT);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  var change = function change(value) {
    if (renderTo && value) {
      updatePosition();
    }
    if (onChange) {
      onChange(value);
    }
  };
  var showTooltip = function showTooltip(val) {
    setShow(val);
    change(val);
  };
  var guardChildClick = function guardChildClick(c) {
    return 'onClick' in c.props;
  };
  var isTouchDevice = function isTouchDevice() {
    var isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return isTouch;
  };
  var handleClick = function handleClick(e) {
    if (!alwaysShow) {
      if (/*#__PURE__*/React.isValidElement(children) && guardChildClick(children)) children.props.onClick(e);
      showTooltip(!show);
    }
  };
  var onMouseEnter = function onMouseEnter() {
    if (!disableHover && !alwaysShow && !isTouchDevice()) {
      if (delay) {
        TOOLTIP_TIMEOUT = setTimeout(function () {
          showTooltip(true);
        }, 800);
      } else {
        showTooltip(true);
      }
    }
  };
  var onMouseLeave = function onMouseLeave() {
    if (delay) {
      clearTimeout(TOOLTIP_TIMEOUT);
    }
    if (!disableHover && !alwaysShow) {
      showTooltip(false);
    }
  };
  var renderTooltipContent = function renderTooltipContent() {
    var tooltipContent = jsx("span", {
      className: classes.wrenchTooltip,
      id: uniqueTooltipName,
      ref: tooltipContentRef,
      role: "tooltip",
      style: tooltipStyle,
      children: jsx("span", {
        className: tooltipBodyClasses,
        children: text
      })
    });
    return handlePortal(tooltipContent, renderTo);
  };
  var newChildren = React.Children.map(children, function (child) {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    if (typeof /*#__PURE__*/React.isValidElement(child) === 'string') {
      return child;
    }
    var childProps = child === null || child === void 0 ? void 0 : child.props;
    if (childProps && childProps.stretched !== stretched) {
      if (childProps.stretched) setStretched(childProps.stretched);
    }
    var displayName = getDisplayName(child);
    var additionalProps = __assign(__assign(__assign({}, displayName && displayName !== 'Button' && {
      tabIndex: 0
    }), displayName ? {
      describedby: uniqueTooltipName
    } : {
      'aria-describedby': uniqueTooltipName
    }), {
      onClick: !disableHover && childProps.onClick ? childProps.onClick : handleClick
    });
    return /*#__PURE__*/React.cloneElement(child, __assign({}, additionalProps));
  });
  return jsxs("span", __assign({
    className: classes.wrenchTooltipContainer
  }, safeRest(rest), {
    children: [renderTooltipContent(), jsx("span", {
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onFocus: onMouseEnter,
      onBlur: onMouseLeave,
      ref: tooltipRef,
      children: newChildren
    })]
  }));
};

export { TOOLTIP_PLACEMENTS, Tooltip };
